import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';

import { FormCard } from '../../../../components';
import { ErrorIcon, ErrorValidation } from '../../../../components/ErrorValidation';
import { logger } from '../../../../features/logger';
import { FinancialAttachmentUploadDialog } from '../../../../shared/dialogConfigs';
import { updateCustomerFinancialCheck } from '../../../../store/customer';
import { CONTROL_TYPE } from '../../../../utils/constants';
import {
  getUploadRequiredValidation,
  handleRemoveFile,
  onRemoveFile,
  openUploadDialog
} from '../../../../utils/fileUploadHelpers';
import AttachmentUpload from '../../../AttachmentUpload';
import type { CardApiService, CardDispatch, CardErrorMustContain, CardStore, TFunc } from '../../types';
const category = 'financial.attachments';

const EMPTY_ARRAY: unknown[] = [];

const textSx = {
  color: 'var(--sprout-text-primary, #101C16)',
  font: 'var(--sprout-font-body1)'
};

const errorValidationSx = {
  ...textSx,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: 1
};

interface CheckBoxProps {
  t: TFunc;
  apiService?: CardApiService;
  dispatch?: CardDispatch;
  store?: CardStore;
  checkuploadButton: (checked: boolean) => void;
  uploadDocumentCount: number;
  isReview?: boolean;
  showErrorBox?: boolean;
}

interface FinancialAttachmentCardProps {
  isReview?: boolean;
  store: CardStore;
  disableAllFields?: boolean;
  errorMustContain?: CardErrorMustContain;
  cardTitle?: string;
  cardSubTitle?: string;
  dispatch?: CardDispatch;
  apiService?: CardApiService;
}

function CheckBox(props: CheckBoxProps) {
  const { t, apiService, dispatch, store, checkuploadButton, uploadDocumentCount, isReview, showErrorBox } = props;

  const customerOptoutAttachments = store?.customer?.customerOptoutAttachments;
  const [checked, setChecked] = useState(Boolean(customerOptoutAttachments));
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    // Keep local state in sync with store updates (e.g. initial load / refresh).
    const nextChecked = Boolean(customerOptoutAttachments);
    setChecked(nextChecked);
    checkuploadButton(nextChecked);
  }, [customerOptoutAttachments, checkuploadButton]);

  const handleChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const nextChecked = event.target.checked;
      const previousChecked = checked;

      const customer = store?.customer || {};
      const updatedCustomer = {
        ...customer,
        customerOptoutAttachments: nextChecked
      };

      (dispatch as CardDispatch)(updateCustomerFinancialCheck(nextChecked));
      setChecked(nextChecked);
      checkuploadButton(nextChecked);

      try {
        setIsSaving(true);
        await apiService.updateCustomer(updatedCustomer, dispatch);
      } catch (error) {
        logger.error('Error updating customer financial check', { error });
        (dispatch as CardDispatch)(updateCustomerFinancialCheck(previousChecked));
        setChecked(previousChecked);
        checkuploadButton(previousChecked);
      } finally {
        setIsSaving(false);
      }
    },
    [apiService, checkuploadButton, checked, dispatch, store]
  );

  const boxMb = '16px';
  const boxMt = isReview && !showErrorBox ? '16px' : 0;
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
        mb: boxMb,
        mt: boxMt
      }}
    >
      <FormControlLabel
        control={
          <Checkbox
            checked={checked}
            onChange={handleChange}
            slotProps={{
              input: {
                'aria-label': t('financial.financialAttachmentCard.financialCheckBox.title')
              }
            }}
            disabled={uploadDocumentCount > 0 || isSaving}
            sx={{ pl: 0, ml: 0 }}
          />
        }
        label={t('financial.financialAttachmentCard.financialCheckBox.title')}
        sx={{
          '& .MuiFormControlLabel-label': {
            color: '#101C16 !important',
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '24px',
            opacity: 1
          },
          m: 0
        }}
        labelPlacement="end"
      />
    </Box>
  );
}

function FinancialAttachmentCard(props: FinancialAttachmentCardProps) {
  const { isReview, store, disableAllFields, errorMustContain, cardTitle, cardSubTitle, dispatch, apiService } = props;
  const lineOfCredit = store?.customer?.lineOfCredit;
  const { t } = useTranslation();
  const [disableUploadButton, setDisableUploadButton] = useState(false);

  const checkuploadButton = useCallback((checkBoxState: boolean) => {
    setDisableUploadButton(checkBoxState);
  }, []);

  const cardId = 'financialCard';
  const field = 'financialCardAttachment';
  const attachments = store?.attachments?.uploads?.[field]?.files ?? EMPTY_ARRAY;
  const required = getUploadRequiredValidation(store, field);
  const uploadDocumentCount = attachments.length;
  const showErrorBox = isReview && !disableUploadButton && uploadDocumentCount === 0;

  const handleDownload = useCallback(
    (hcId: string | number | undefined, fileName: string) => apiService.downloadFile(hcId, fileName, dispatch),
    [apiService, dispatch]
  );

  const handleUpload = useMemo(
    () =>
      openUploadDialog({
        dispatch: dispatch as CardDispatch,
        apiService,
        // Dialog config's published DialogEvent types don't line up with the
        // upload/cancel callbacks; runtime is correct.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dialog config uses DialogEvent handlers that don't match the helper's UploadDialog type; runtime is correct
        uploadDialog: FinancialAttachmentUploadDialog as any,
        field
      }),
    [apiService, dispatch, field]
  );

  const handleRemove = useMemo(
    () =>
      handleRemoveFile(dispatch as CardDispatch, apiService, () =>
        onRemoveFile(field, required, attachments, dispatch as CardDispatch, t)
      ),
    [apiService, attachments, dispatch, field, required, t]
  );

  const attachmentsConfig = useMemo(
    () => ({
      store,
      category,
      field,
      type: CONTROL_TYPE.attachmentList,
      isAdditionalAttachment: true,
      label: t('address.taxExempt.attachments.label'),
      handleRemove,
      handleUpload,
      handleDownload,
      shouldShow: true,
      required,
      dispatch: dispatch as CardDispatch,
      listRight: true,
      attachments,
      showUploadButton: true
    }),
    [attachments, dispatch, field, handleDownload, handleRemove, handleUpload, required, store, t]
  );

  const cardProps = useMemo(
    () => ({
      cardId,
      title: t(cardTitle || 'financial.financialAttachmentCard.title'),
      subtitle: t(cardSubTitle || 'financial.financialAttachmentCard.subtitle'),
      image: '/food/onboarding/assets/Attachments.svg',
      isReview,
      store,
      errorMustContain
    }),
    [cardId, cardSubTitle, cardTitle, errorMustContain, isReview, store, t]
  );

  // If the customer doesn't have a line of credit, this card has nothing to show.
  // Return null (instead of an empty fragment) so ReviewAccordion can't render a blank block.
  if (!lineOfCredit) return null;
  return (
    <FormCard {...cardProps}>
      {showErrorBox && (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Box has no `as` prop in its published types; styled passthrough, runtime is correct
        <Box sx={{ ...errorValidationSx, mb: '16px', mt: '16px' }} {...({ as: ErrorValidation } as any)}>
          <ErrorIcon />
          <Typography component="span" sx={textSx}>
            {t('financial.financialAttachmentCard.financialCheckBox.financialCardError')}
          </Typography>
        </Box>
      )}
      <CheckBox
        t={t}
        {...props}
        uploadDocumentCount={uploadDocumentCount}
        checkuploadButton={checkuploadButton}
        isReview={isReview}
        showErrorBox={showErrorBox}
      />
      <AttachmentUpload
        disabled={disableAllFields || disableUploadButton}
        disableUploadButton={disableUploadButton}
        {...attachmentsConfig}
      />
    </FormCard>
  );
}

export default FinancialAttachmentCard;
